/**
 * The Activities catalog: one typed entry per activity. This file (plus
 * runs.ts/runsSummary.ts) is the platform's ONLY public surface; everything
 * under lib/activities/<slug>/ and app/activities/<slug>/ is private to that
 * activity, enforced by ESLint (see README.md in this folder).
 *
 * Adding an activity = one entry here + its two folders + its own tables.
 * Destroying one = delete the entry and follow README.md's runbook.
 */

export type ActivitySlug =
  | "scenarios"
  | "teach-back"
  | "voice-capsule"
  | "phrase-chunks"
  | "false-cognates"
  | "conjugation-gym"
  | "collocations"
  | "delayed-recall"
  | "minimal-pairs"
  | "spoken-numbers"
  | "shadowing";

/**
 * Target locales whose teach-back corpus has been hand-authored. Inlined rather
 * than read from the activity's content module: the registry must not reach
 * into activity internals (that is the whole point of the boundary). A
 * colocated test in the activity pins this set to the corpus, so the two cannot
 * drift silently.
 */
const TEACH_BACK_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored anchor passage. Same boundary reasoning as above. */
const VOICE_CAPSULE_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored chunk corpus. Same boundary reasoning as above. */
const PHRASE_CHUNK_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored false-cognate corpus. Same boundary reasoning. */
const FALSE_COGNATE_LOCALES = new Set(["pt-BR"]);

/** Locales with hand-checked verb tables. Same boundary reasoning. */
const CONJUGATION_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored collocation corpus. Same boundary reasoning. */
const COLLOCATION_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored contrast corpus. Same boundary reasoning. */
const MINIMAL_PAIR_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored shadowing corpus. Same boundary reasoning as above. */
const SHADOWING_LOCALES = new Set(["pt-BR"]);

/**
 * Locales whose numbers this app can actually SPEAK.
 *
 * Unlike its neighbours, this set does not gate a corpus - spoken-numbers has none, it
 * generates every item. It gates the number speller: `numbers.ts` knows how to say
 * "mil duzentos e cinquenta" and nothing else. Adding a locale here without teaching
 * that module its number words would ship a drill that reads Portuguese words at a
 * learner of another language. A colocated test pins this set to the speller.
 */
const SPOKEN_NUMBER_LOCALES = new Set(["pt-BR"]);

/**
 * Each activity owns one palette color. Seven colors, nine activities, so two pairs
 * deliberately share: teach-back with delayed-recall (both are memory work), and
 * phrase-chunks with collocations (both are about which words go together). Each pair
 * sits far apart in the grid, so two cards of the same color never touch.
 */
export type ActivityAccent =
  | "coral"
  | "reading"
  | "writing"
  | "speaking"
  | "listening"
  | "lavender"
  | "recording";

export type ActivityDefinition = {
  slug: ActivitySlug;
  /** i18n key; copy never lives inline. */
  titleKey: string;
  /** i18n key; copy never lives inline. */
  descriptionKey: string;
  /** Material Symbol name; must be in the app/layout.tsx preload list. */
  icon: string;
  /** Palette color for this activity's card and page. */
  accent: ActivityAccent;
  href: string;
  /** "retired" = soft-kill: hidden from the catalog, code and data remain. */
  status: "active" | "retired";
  /** True when usable content exists for this target locale. */
  availableFor: (targetLocale: string) => Promise<boolean>;
};

export const ACTIVITIES: Record<ActivitySlug, ActivityDefinition> = {
  scenarios: {
    slug: "scenarios",
    titleKey: "activities.scenarios.title",
    descriptionKey: "activities.scenarios.description",
    icon: "forum",
    accent: "coral", // the flagship; conversation is the app's primary color
    href: "/activities/scenarios",
    status: "active",
    // Scenario content is generated at runtime for any locale.
    availableFor: async () => true,
  },
  "teach-back": {
    slug: "teach-back",
    titleKey: "activities.teach-back.title",
    descriptionKey: "activities.teach-back.description",
    icon: "school",
    accent: "lavender",
    href: "/activities/teach-back",
    status: "active",
    availableFor: async (targetLocale) => TEACH_BACK_LOCALES.has(targetLocale),
  },
  "voice-capsule": {
    slug: "voice-capsule",
    titleKey: "activities.voice-capsule.title",
    descriptionKey: "activities.voice-capsule.description",
    icon: "mic",
    accent: "speaking", // it is literally the speaking skill
    href: "/activities/voice-capsule",
    status: "active",
    availableFor: async (targetLocale) => VOICE_CAPSULE_LOCALES.has(targetLocale),
  },
  "phrase-chunks": {
    slug: "phrase-chunks",
    titleKey: "activities.phrase-chunks.title",
    descriptionKey: "activities.phrase-chunks.description",
    icon: "exercise",
    accent: "reading",
    href: "/activities/phrase-chunks",
    status: "active",
    availableFor: async (targetLocale) => PHRASE_CHUNK_LOCALES.has(targetLocale),
  },
  "false-cognates": {
    slug: "false-cognates",
    titleKey: "activities.false-cognates.title",
    descriptionKey: "activities.false-cognates.description",
    icon: "psychology",
    accent: "recording", // Danger Zone. The copy says these words "betray you".
    href: "/activities/false-cognates",
    status: "active",
    availableFor: async (targetLocale) => FALSE_COGNATE_LOCALES.has(targetLocale),
  },
  "conjugation-gym": {
    slug: "conjugation-gym",
    titleKey: "activities.conjugation-gym.title",
    descriptionKey: "activities.conjugation-gym.description",
    icon: "exercise",
    accent: "writing",
    href: "/activities/conjugation-gym",
    status: "active",
    availableFor: async (targetLocale) => CONJUGATION_LOCALES.has(targetLocale),
  },
  "minimal-pairs": {
    slug: "minimal-pairs",
    titleKey: "activities.minimal-pairs.title",
    descriptionKey: "activities.minimal-pairs.description",
    icon: "hearing",
    // Ear Training is the listening skill, so it takes the listening color outright.
    accent: "listening",
    href: "/activities/minimal-pairs",
    status: "active",
    availableFor: async (targetLocale) => MINIMAL_PAIR_LOCALES.has(targetLocale),
  },
  "delayed-recall": {
    slug: "delayed-recall",
    titleKey: "activities.delayed-recall.title",
    descriptionKey: "activities.delayed-recall.description",
    icon: "visibility_off",
    accent: "lavender", // the deliberate reuse; see ActivityAccent
    href: "/activities/delayed-recall",
    status: "active",
    // No corpus: the passage is generated per session, so any target locale the
    // model writes works. Same reasoning as scenarios, and the reason this is
    // the only activity since scenarios with no *_LOCALES set above.
    availableFor: async () => true,
  },
  collocations: {
    slug: "collocations",
    titleKey: "activities.collocations.title",
    descriptionKey: "activities.collocations.description",
    icon: "assignment",
    // Shares reading with phrase-chunks: both are about which words go together. The
    // listening color went to Ear Training, which is that skill outright.
    accent: "reading",
    href: "/activities/collocations",
    status: "active",
    availableFor: async (targetLocale) => COLLOCATION_LOCALES.has(targetLocale),
  },
  "spoken-numbers": {
    slug: "spoken-numbers",
    titleKey: "activities.spoken-numbers.title",
    descriptionKey: "activities.spoken-numbers.description",
    icon: "payments",
    // The second listening activity, and it shares the color with Ear Training on
    // purpose: both are ear work. The catalog is a 2-column grid, so at these two
    // positions (7th and 10th) the cards sit diagonally, never touching - which is
    // the rule the ActivityAccent comment above is protecting.
    accent: "listening",
    href: "/activities/spoken-numbers",
    status: "active",
    availableFor: async (targetLocale) => SPOKEN_NUMBER_LOCALES.has(targetLocale),
  },
  shadowing: {
    slug: "shadowing",
    titleKey: "activities.shadowing.title",
    descriptionKey: "activities.shadowing.description",
    icon: "graphic_eq",
    // Shares speaking with voice-capsule: both are the learner's own voice. In the
    // 2-column catalog they sit at positions 3 and 11, nowhere near each other.
    accent: "speaking",
    href: "/activities/shadowing",
    status: "active",
    availableFor: async (targetLocale) => SHADOWING_LOCALES.has(targetLocale),
  },
};

export function activityHref(slug: ActivitySlug): string {
  return ACTIVITIES[slug].href;
}

/** Active + available entries, in registry (display) order. */
export async function activeActivities(targetLocale: string): Promise<ActivityDefinition[]> {
  const defs = Object.values(ACTIVITIES).filter((a) => a.status === "active");
  const availability = await Promise.all(defs.map((a) => a.availableFor(targetLocale)));
  return defs.filter((_, i) => availability[i]);
}
